import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Head from 'next/head';
import {useAnalytics} from 'mkt-ssr/lib/analytics';
import {isServer} from 'mkt-ssr/lib/env';
import {ExperimentData, ExperimentId} from 'mkt-ssr/lib/experiments';

const NO_PAGE_ID = 'no-page-id';

interface ExperimentsContextValue {
  experimentData: ExperimentData;
  experimentsPageId: string | undefined;
  setExperimentsPageId: (pageId: string) => void;
}

const ExperimentsContext = createContext<ExperimentsContextValue>({
  experimentData: {},
  experimentsPageId: '',
  setExperimentsPageId: () => null,
});

export function useExperiments() {
  return useContext(ExperimentsContext);
}

export function useSetExperimentsPageId(): (pageId: string) => void {
  const context = useContext(ExperimentsContext);
  if (!context) {
    throw new Error(
      'useSetExperimentsPageId must be used within ExperimentsProvider',
    );
  }
  return context.setExperimentsPageId;
}

export function useReportManualExposure(experimentId: ExperimentId) {
  const {experimentData, experimentsPageId} = useExperiments();
  const {reportExperimentExposure} = useAnalytics();
  return () => {
    reportExperimentExposure(
      {
        experimentId,
        arbId: experimentData[experimentId]?.arb_id || '',
        variantId: experimentData[experimentId]?.variant_id || '',
      },
      experimentsPageId || NO_PAGE_ID,
    );
  };
}

const generateExperimentDataWithFreshArbIds = (
  experimentData: ExperimentData,
  experimentTreatments: string,
): ExperimentData => {
  const experimentDataNew = JSON.parse(JSON.stringify(experimentData));

  experimentTreatments.split(',').forEach((treatment) => {
    const treatmentData = treatment.split('.');
    const experimentId = treatmentData[0] as ExperimentId;
    const arbId = treatmentData[3];
    if (experimentDataNew[experimentId]) {
      experimentDataNew[experimentId].arb_id = arbId;
    }
  });

  return experimentDataNew;
};

export const ExperimentsProvider = ({
  children,
  experimentData = {},
}: {
  children: ReactNode;
  experimentData: ExperimentData;
}) => {
  const [experimentDataHydrated, setExperimentDataHydrated] = useState<
    ExperimentData | undefined
  >();

  const [experimentsPageId, setExperimentsPageId] = useState<
    string | undefined
  >();
  const setExperimentsPageIdCallback = useCallback(
    (newExperimentsPageId: string) => {
      setExperimentsPageId(newExperimentsPageId);
    },
    [],
  );

  const [reportedAutomaticExposures, setReportedAutomaticExposures] =
    useState<boolean>(false);
  const {reportExperimentExposure} = useAnalytics();

  // This is necessary because of our usage of experiment middleware
  // (see mkt/Experiments/middleware/experiment_middleware.rb)
  const experimentTreatments = useMemo(() => {
    if (!isServer()) {
      return document.querySelector<HTMLMetaElement>(
        'meta[name="experiment-treatments"]',
      )?.content;
    }
  }, []);

  // This is necessary because of our edge experimentation implementation
  const edgeExperimentTreatments = useMemo(() => {
    if (!isServer()) {
      return document.querySelector<HTMLMetaElement>(
        'meta[name="edge-experiment-treatments"]',
      )?.content;
    }
  }, []);

  // This is necessary because of our usage of experiment middleware and our
  // edge experimentation implementation
  const experimentToken = useMemo(() => {
    if (!isServer()) {
      return document.querySelector<HTMLMetaElement>(
        'meta[name="experiment-token"]',
      )?.content;
    }
  }, []);

  // This useEffect hydrates experimentData with fresh arb_ids
  useEffect(() => {
    if (edgeExperimentTreatments) {
      setExperimentDataHydrated(
        generateExperimentDataWithFreshArbIds(
          experimentData,
          edgeExperimentTreatments,
        ),
      );
    } else if (experimentTreatments) {
      setExperimentDataHydrated(
        generateExperimentDataWithFreshArbIds(
          experimentData,
          experimentTreatments,
        ),
      );
    }
  }, [experimentData, experimentTreatments, edgeExperimentTreatments]);

  // This useEffect executes automatic exposure reporting
  useEffect(() => {
    if (!isServer() && !reportedAutomaticExposures && experimentDataHydrated) {
      Object.values(experimentDataHydrated)
        .filter((experimentDataValue) => !experimentDataValue.manual_exposure)
        .forEach((experimentDataValue) => {
          reportExperimentExposure(
            {
              experimentId: experimentDataValue.experiment_id,
              arbId: experimentDataValue.arb_id,
              variantId: experimentDataValue.variant_id,
            },
            experimentsPageId || NO_PAGE_ID,
          );
        });

      setReportedAutomaticExposures(true);
    }
  }, [
    experimentDataHydrated,
    experimentsPageId,
    reportedAutomaticExposures,
    reportExperimentExposure,
  ]);

  return (
    <>
      <Head>
        <meta
          name="edge-experiment-treatments"
          content={edgeExperimentTreatments || ''}
        />
        <meta
          name="experiment-treatments"
          content={experimentTreatments || ''}
        />
        <meta name="experiment-token" content={experimentToken || ''} />
      </Head>
      <ExperimentsContext.Provider
        value={{
          experimentData: experimentDataHydrated || experimentData,
          experimentsPageId,
          setExperimentsPageId: setExperimentsPageIdCallback,
        }}
      >
        {children}
      </ExperimentsContext.Provider>
    </>
  );
};
